import React, { useState, useEffect } from "react";
import { Package, Building2, Users, CheckCircle, Store, TrendingUp, ShoppingCart, Calendar } from "lucide-react";
import { useLanguage } from '../context/LanguageContext';
import API_BASE_URL from '../services/apiconfig';

const MudhalvarMarunthagam = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [mudhalvarData, setMudhalvarData] = useState(null);
  const [metrics, setMetrics] = useState({
    totalShops: 0,
    totalSales: 0,
    avgDailySales: 0,
    totalBeneficiaries: 0,
    sundaySales: 0,
    totalInvoices: 0
  });
  const [metricsLoading, setMetricsLoading] = useState(true);

  const MEDIA_BASE_URL = 'https://kooturavu.tn.gov.in';
  const MUMA_PUBLIC_API = 'https://dashboard.kooturavu.tn.gov.in/v1/api/muma/get-public-count';
  const API_KEY = 'eD5WnZ_H4j9KxM3bT6rG8vY2pL1aC7uF0sQZ5eA3oB4iD7gC9hE0f2jK1lM8n7O6P4qR';

  useEffect(() => {
    fetchMudhalvarData();
    fetchMetrics();
  }, []);

  const fetchMudhalvarData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/mudhalvar_marunthagam/get_content`);
      const result = await response.json();
      
      if (result.status === 'success' && result.data) {
        setMudhalvarData(result.data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching Mudhalvar Marunthagam data:', error);
      setLoading(false);
    }
  };

  const fetchMetrics = async () => {
    try {
      const response = await fetch(MUMA_PUBLIC_API, {
        headers: {
          'x-api-key': API_KEY
        }
      });

      const data = await response.json();

      if (data) {
        setMetrics({
          totalShops: data.total_shops_count || 0,
          totalSales: data.all_shops_total_sales_values || 0,
          avgDailySales: data.average_per_day || 0,
          totalBeneficiaries: data.total_invoice_count || 0,
          sundaySales: data.total_sunday_sales || 0,
          totalInvoices: data.total_invoice_count || 0
        });
      }
      
      setMetricsLoading(false);
    } catch (error) {
      console.error('Error fetching metrics:', error);
      setMetricsLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-IN').format(Math.round(value));
  };

  const content = {
    en: {
      overview: "Overview",
      whatWeOffer: "What We Offer",
      howItWorks: "How It Works",
      keyStats: "Key Stats",
      liveMetrics: "Live Performance Metrics",
      totalShops: "Total Shops",
      totalSales: "Total Sales",
      avgDailySales: "Avg Daily Sales",
      beneficiaries: "Beneficiaries",
      sundaySales: "Sunday Sales",
      loading: "Loading metrics..."
    },
    ta: {
      overview: "கண்ணோட்டம்",
      whatWeOffer: "நாங்கள் வழங்குவது",
      howItWorks: "இது எவ்வாறு செயல்படுகிறது",
      keyStats: "முக்கிய புள்ளிவிவரங்கள்",
      liveMetrics: "நேரடி செயல்திறன் அளவீடுகள்",
      totalShops: "மொத்த கடைகள்",
      totalSales: "மொத்த விற்பனை",
      avgDailySales: "சராசரி தினசரி விற்பனை",
      beneficiaries: "பயனாளிகள்",
      sundaySales: "ஞாயிறு விற்பனை",
      loading: "அளவீடுகள் ஏற்றப்படுகின்றன..."
    }
  };

  const t = content[language];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (!mudhalvarData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">No Mudhalvar Marunthagam data available</div>
      </div>
    );
  }

  return (
    <section id="mudhalvar-section" className="bg-white p-8 rounded-lg shadow-sm">

      {/* Overview */}
      <div className="mb-10">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.overview}</h3>
        <div className="bg-blue-50 p-6 rounded-lg">
          <p className="text-gray-700 leading-relaxed">
            {language === 'en' ? mudhalvarData.overview_eng : mudhalvarData.overview_tamil}
          </p>
        </div>
      </div>

      {/* Vision/Content Box */}
      {(mudhalvarData.content_eng || mudhalvarData.content_tamil) && (
        <div className="mb-10">
          <div className="border-2 border-dashed border-blue-300 bg-blue-50 p-6 rounded-lg max-w-4xl mx-auto">
            <p className="text-gray-700 leading-relaxed italic text-center">
              {language === 'en' ? mudhalvarData.content_eng : mudhalvarData.content_tamil}
            </p>
          </div>
        </div>
      )}

{/* Live Metrics Section */}
      <div className="mb-10">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.liveMetrics}</h3>
        {metricsLoading ? (
          <div className="text-center py-8 text-gray-600">{t.loading}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 border border-indigo-200 p-6 rounded-lg shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <Store size={32} className="text-indigo-600" />
                <div className="text-right">
                  <div className="text-3xl font-bold text-indigo-700">{formatNumber(metrics.totalShops)}</div>
                  <div className="text-sm text-indigo-600 mt-1">{t.totalShops}</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 p-6 rounded-lg shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp size={32} className="text-amber-600" />
                <div className="text-right">
                  <div className="text-2xl font-bold text-amber-700">{formatCurrency(metrics.totalSales)}</div>
                  <div className="text-sm text-amber-600 mt-1">{t.totalSales}</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 p-6 rounded-lg shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <ShoppingCart size={32} className="text-blue-600" />
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-700">{formatCurrency(metrics.avgDailySales)}</div>
                  <div className="text-sm text-blue-600 mt-1">{t.avgDailySales}</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 p-6 rounded-lg shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <Users size={32} className="text-emerald-600" />
                <div className="text-right">
                  <div className="text-3xl font-bold text-emerald-700">{formatNumber(metrics.totalBeneficiaries)}</div>
                  <div className="text-sm text-emerald-600 mt-1">{t.beneficiaries}</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-violet-50 to-violet-100 border border-violet-200 p-6 rounded-lg shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <Calendar size={32} className="text-violet-600" />
                <div className="text-right">
                  <div className="text-2xl font-bold text-violet-700">{formatCurrency(metrics.sundaySales)}</div>
                  <div className="text-sm text-violet-600 mt-1">{t.sundaySales}</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* What We Offer */}
      {mudhalvarData.what_we_offer && mudhalvarData.what_we_offer.length > 0 && (
        <div className="mb-10">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.whatWeOffer}</h3>
          <div className="bg-green-50 p-6 rounded-lg">
            <ul className="space-y-3">
              {mudhalvarData.what_we_offer.map((offer, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-green-600 mr-3">•</span>
                  <span className="text-gray-700">
                    {language === 'en' ? offer.content_eng : offer.content_tamil}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* How It Works */}
      {mudhalvarData.how_its_works && mudhalvarData.how_its_works.length > 0 && (
        <div className="mb-10">
          <h3 className="text-lg font-semibold text-gray-800 mb-6">{t.howItWorks}</h3>
          <div className="bg-purple-50 p-6 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              
              {mudhalvarData.how_its_works.map((step, index) => {
                const icons = [Package, Building2, Building2, Users];
                const bgColors = ["bg-blue-100 text-blue-600", "bg-purple-100 text-purple-600", "bg-green-100 text-green-600", "bg-amber-100 text-amber-600"];
                const Icon = icons[index % icons.length];

                return (
                  <div key={index} className="text-center">
                    <div className="bg-white rounded-lg p-6 shadow-sm">
                      {step.logo_path ? (
                        <div className="flex justify-center mb-3">
                          <img
                            src={`${MEDIA_BASE_URL}/${step.logo_path}`}
                            alt="step icon"
                            className="w-16 h-16 object-contain"
                          />
                        </div>
                      ) : (
                        <div className={`inline-flex items-center justify-center w-16 h-16 ${bgColors[index % bgColors.length]} rounded-full mb-3`}>
                          <Icon size={28} />
                        </div>
                      )}
                      <div className="font-bold text-gray-900 text-lg mb-2">
                        {language === 'en' ? step.heading_eng : step.heading_tamil}
                      </div>
                      <div className="text-sm text-gray-600">
                        {language === 'en' ? step.content_eng : step.content_tamil}
                      </div>
                    </div>
                  </div>
                );
              })}

            </div>
          </div>
        </div>
      )}

      {/* Key Stats */}
      {mudhalvarData.key_stats && mudhalvarData.key_stats.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.keyStats}</h3>
          <div className="flex justify-center">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full max-w-7xl">
              {mudhalvarData.key_stats.map((item, i) => {
                const bgColors = ["bg-sky-100", "bg-indigo-100", "bg-violet-100", "bg-cyan-100", "bg-teal-100"];
                
                return (
                  <div key={i} className={`${bgColors[i % bgColors.length]} p-6 text-center rounded-lg w-full`}>
                    {item.logo_path ? (
                      <div className="flex justify-center mb-3">
                        <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                          <img
                            src={`${MEDIA_BASE_URL}/${item.logo_path}`}
                            alt="stat icon"
                            className="w-8 h-8 object-contain"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-center mb-3">
                        <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-gray-700">
                          <CheckCircle />
                        </div>
                      </div>
                    )}
                    <div className="text-2xl font-bold text-gray-900">
                      {language === 'en' ? item.heading_eng : item.heading_tamil}
                    </div>
                    <div className="text-sm font-semibold text-gray-700 mt-2">
                      {language === 'en' ? item.content_eng : item.content_tamil}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

    </section>
  );
};

export default MudhalvarMarunthagam;